import { useMemo, useState } from "react";

import { Colors } from "@blueprintjs/core";
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { roundToOneDecimalPlace } from "@/components/pages_logged_in/program/components/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { CARD_CLASSES, LIGHT_GRAY, LIGHT_GREEN } from "@/css/constants";
import { GetCreditUsageQuery, useGetCreditUsageQuery } from "@/graphql";
import { MainComponent } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

const COLOR_MAP = {
  totalSpent: "#000000",
  completedCredits: "#0D8050",
  scheduledCredits: "#215DB0",
  forfeitedCredits: "#C61D1D",
};

interface StatCardProps {
  title: string;
  value?: string | null;
  backgroundColor: string;
  color: string;
}

function StatCard({ title, value, backgroundColor, color }: StatCardProps) {
  return (
    <Box
      css={{
        backgroundColor,
        borderRadius: "10px",
        padding: "24px",
        flex: 1,
        minWidth: "200px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Box css={{ color: color, fontSize: "16px", fontWeight: 400 }}>
        {title}
      </Box>
      <Flex alignItems="center" css={{ gap: "12px" }}>
        <EmbedSvg src={CreditSVG} height={24} width={24} />
        <Box
          css={{
            color: color,
            fontSize: "32px",
            fontWeight: 600,
            lineHeight: "1em",
          }}
        >
          {value || 0}
        </Box>
      </Flex>
    </Box>
  );
}

type CreditUsageByComponent = NonNullableArrayItem<
  NonNullable<GetCreditUsageQuery["creditUsage"]>["creditUsageByComponent"]
>;

interface CreditUsageProps {
  component: MainComponent;
}

export function CreditUsage({ component }: CreditUsageProps) {
  const { data, loading } = useGetCreditUsageQuery({
    variables: {
      programComponentId: component.id,
    },
  });

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "name",
      desc: false,
    },
  ]);

  const { totalSpent, completedCredits, scheduledCredits, forfeitedCredits } =
    data?.creditUsage || {};

  const componentData = useMemo(() => {
    return ensureNonNullArray(data?.creditUsage?.creditUsageByComponent);
  }, [data?.creditUsage?.creditUsageByComponent]);

  const columns: ColumnDef<CreditUsageByComponent>[] = [
    {
      id: "name",
      header: ({
        column,
      }: {
        column: Column<CreditUsageByComponent, unknown>;
      }) => <DataTableColumnHeader column={column} title="Component" />,
      accessorKey: "name",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.name?.toLowerCase() || "";
        const nameB = rowB.original.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      cell: ({ row }: { row: Row<CreditUsageByComponent> }) => {
        return <Box>{row.original.name}</Box>;
      },
    },
    {
      id: "totalSpent",
      header: ({
        column,
      }: {
        column: Column<CreditUsageByComponent, unknown>;
      }) => <DataTableColumnHeader column={column} title="Total Spent" />,
      accessorKey: "totalSpent",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const valueA = rowA.original.totalSpent || 0;
        const valueB = rowB.original.totalSpent || 0;
        return valueA - valueB;
      },
      cell: ({ row }: { row: Row<CreditUsageByComponent> }) => {
        return (
          <Flex alignItems="center" style={{ gap: 8 }}>
            <EmbedSvg src={CreditSVG} height={16} width={16} />
            {roundToOneDecimalPlace(row.original.totalSpent ?? 0)}
          </Flex>
        );
      },
    },
    {
      id: "completedCredits",
      header: ({
        column,
      }: {
        column: Column<CreditUsageByComponent, unknown>;
      }) => (
        <DataTableColumnHeader column={column} title="Completed Sessions" />
      ),
      accessorKey: "completedCredits",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const valueA = rowA.original.completedCredits || 0;
        const valueB = rowB.original.completedCredits || 0;
        return valueA - valueB;
      },
      cell: ({ row }: { row: Row<CreditUsageByComponent> }) => {
        return (
          <Flex alignItems="center" style={{ gap: 8 }}>
            <EmbedSvg src={CreditSVG} height={16} width={16} />
            {roundToOneDecimalPlace(row.original.completedCredits ?? 0)}
          </Flex>
        );
      },
    },
    {
      id: "scheduledCredits",
      header: ({
        column,
      }: {
        column: Column<CreditUsageByComponent, unknown>;
      }) => (
        <DataTableColumnHeader column={column} title="Scheduled Sessions" />
      ),
      accessorKey: "scheduledCredits",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const valueA = rowA.original.scheduledCredits || 0;
        const valueB = rowB.original.scheduledCredits || 0;
        return valueA - valueB;
      },
      cell: ({ row }: { row: Row<CreditUsageByComponent> }) => {
        return (
          <Flex alignItems="center" style={{ gap: 8 }}>
            <EmbedSvg src={CreditSVG} height={16} width={16} />
            {roundToOneDecimalPlace(row.original.scheduledCredits ?? 0)}
          </Flex>
        );
      },
    },
    {
      id: "forfeitedCredits",
      header: ({
        column,
      }: {
        column: Column<CreditUsageByComponent, unknown>;
      }) => <DataTableColumnHeader column={column} title="Forfeited" />,
      accessorKey: "forfeitedCredits",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const valueA = rowA.original.forfeitedCredits || 0;
        const valueB = rowB.original.forfeitedCredits || 0;
        return valueA - valueB;
      },
      cell: ({ row }: { row: Row<CreditUsageByComponent> }) => {
        return (
          <Flex alignItems="center" style={{ gap: 8 }}>
            <EmbedSvg src={CreditSVG} height={16} width={16} />
            {roundToOneDecimalPlace(row.original.forfeitedCredits ?? 0)}
          </Flex>
        );
      },
    },
  ];

  const table = useReactTable({
    data: componentData,
    columns,
    state: {
      sorting,
    },
    enableSorting: true,
    enableHiding: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row?.id || "",
  });

  const loadingSkeleton = (
    <div className="flex flex-col gap-4 h-full w-full">
      {Array.from({ length: 5 }).map((_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );

  return (
    <Box
      px={[16, 24]}
      py={[16, 24]}
      flex={1}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
    >
      <Box fontSize={18} fontWeight={600}>
        Credit Usage
      </Box>
      <Flex mt={24} flexWrap="wrap" css={{ gap: "10px" }}>
        <StatCard
          title="Total Spent"
          value={totalSpent}
          backgroundColor={LIGHT_GRAY}
          color={COLOR_MAP.totalSpent}
        />
        <StatCard
          title="Completed Sessions"
          value={completedCredits}
          backgroundColor={LIGHT_GREEN}
          color={COLOR_MAP.completedCredits}
        />
        <StatCard
          title="Scheduled Sessions"
          value={scheduledCredits}
          backgroundColor="#E9F1FB"
          color={COLOR_MAP.scheduledCredits}
        />
        <StatCard
          title="Forfeited"
          value={forfeitedCredits}
          backgroundColor="#F5E6E8"
          color={COLOR_MAP.forfeitedCredits}
        />
      </Flex>

      <Box mt={24}>
        <DataTable
          table={table}
          className="overflow-visible"
          pagination={false}
          emptyState={
            <div className="flex flex-col items-center justify-center py-8">
              {loading ? (
                loadingSkeleton
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-500">
                  No data
                </div>
              )}
            </div>
          }
        />
      </Box>
    </Box>
  );
}
